import React, { useEffect, useRef, useState } from 'react';
import { useDataProvider, useGetIdentity } from 'react-admin';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, StickyNote } from 'lucide-react';
import { CustomerEvent } from '../../types';
import { createNote } from './notesApi';

interface AddNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided, the note is attached to this lead instead of a funeral */
  leadId?: string;
  funeralId?: number;
  onNoteAdded?: (event: CustomerEvent) => void;
}

/**
 * Standalone internal-note composer. Deliberately has no send path of any kind —
 * for staff who'd rather not risk leaving the shared input bar on the SMS
 * channel. The channel picker in MessageInputBar still works as before.
 */
export function AddNoteDialog({
  open,
  onOpenChange,
  leadId,
  funeralId,
  onNoteAdded,
}: AddNoteDialogProps) {
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Clear the draft whenever the dialog is reopened.
  useEffect(() => {
    if (open) {
      setMessage('');
      const t = setTimeout(() => textareaRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  const hasEntityId = !!leadId || !!funeralId;
  const canSave = !!message.trim() && !saving && hasEntityId;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const event = await createNote({
        dataProvider,
        message: message.trim(),
        leadId,
        funeralId,
        userName: identity?.fullName || undefined,
      });
      setMessage('');
      onOpenChange(false);
      if (onNoteAdded) onNoteAdded(event);
    } catch (err: any) {
      console.error('Failed to add note:', err);
      alert(`Failed to add note: ${err?.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Enter inserts a newline here (notes are often multi-line);
    // ⌘/Ctrl+Enter saves.
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void handleSave();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="cleo-ui sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StickyNote className="h-4 w-4" />
            Add internal note
          </DialogTitle>
          <DialogDescription>
            Visible to staff only — nothing is sent to the family.
          </DialogDescription>
        </DialogHeader>

        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add an internal note..."
          rows={5}
          disabled={saving}
          className="w-full resize-none rounded-lg border bg-muted/30 px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
        />

        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="outline" disabled={saving} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" disabled={!canSave} onClick={() => void handleSave()} className="gap-1.5">
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <StickyNote className="h-3.5 w-3.5" />
            )}
            Add note
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AddNoteDialog;
